import api from '../../services/api';
import { EventoRisco, EventoRiscoLog } from '../../services/tipos';

export interface OcorrenciaAtiva {
    id?: string; // exigido pelo DataGrid (= idOcorrencia)
    idOcorrencia: string;
    idEventoRisco: string;
    nomeEvento: string;
    idVeiculo: string;
    placa: string;
    situacao: 'detectada' | 'avisando' | 'enviado' | 'bloqueado' | 'impedido';
    avisosEnviados: number;
    totalAvisos: number;
    ultimoAcionamentoMsg?: string;
    ultimoAcionamentoData?: string;
    impedidoAte?: string | null;
    tipoEvento: number;
}

export const verificarModulo = (idEmpresa: string) =>
    api.get(`/GestaoRiscoModulo/${idEmpresa}`).then(r => Boolean(r.data?.habilitado));

export const listarEventos = (idEmpresa: string) =>
    api.get(`/EventoRiscoPorEmpresa/${idEmpresa}`).then(r => r.data as EventoRisco[]);

export const excluirEvento = (id: string, idEmpresa: string) =>
    api.delete(`/EventoRisco/${id}?idEmpresa=${idEmpresa}`);

export const pausarEvento = (id: string, idEmpresa: string, todos: boolean) =>
    api.post(`/EventoRisco/${id}/Pausar?idEmpresa=${idEmpresa}`, { todos, veiculos: [], locatarios: [] });

export const retomarEvento = (id: string, idEmpresa: string, todos: boolean) =>
    api.post(`/EventoRisco/${id}/Retomar?idEmpresa=${idEmpresa}`, { todos, veiculos: [], locatarios: [] });

export const desbloquearManual = (idEmpresa: string, idVeiculo: string) =>
    api.post(`/EventoRisco/DesbloquearManual`, { idEmpresa, idVeiculo }).then(r => r.data);

export const buscarLog = (idEmpresa: string, filtros: Record<string, any>) =>
    api.get(`/EventoRiscoLog/${idEmpresa}`, { params: filtros }).then(r => r.data as { total: number; itens: EventoRiscoLog[] });

export const listarOcorrenciasAtivas = (idEmpresa: string) =>
    api.get(`/EventoRiscoOcorrenciasAtivas/${idEmpresa}`).then(r => r.data as OcorrenciaAtiva[]);

export const impedirOcorrencia = (idOcorrencia: string, idEmpresa: string, impedidoAte: string | null) =>
    api.post(`/EventoRiscoOcorrencia/${idOcorrencia}/Impedir`, { idEmpresa, impedidoAte });

export const reativarOcorrencia = (idOcorrencia: string, idEmpresa: string) =>
    api.post(`/EventoRiscoOcorrencia/${idOcorrencia}/Reativar`, { idEmpresa });

export const buscarEmpresa = (idEmpresa: string) =>
    api.get(`/Empresa/${idEmpresa}`).then(r => r.data);

// Resolve o centro aproximado da cidade da empresa (Photon → Nominatim, mesma
// abordagem do MapaCercaPicker). Usado para semear a origem do modelo "Fuga da Região".
export const geocodificarCidade = async (
    empresa: { cidade?: string; estado?: string } | null | undefined
): Promise<{ lat: number; lng: number } | null> => {
    const q = [empresa?.cidade, empresa?.estado].filter(Boolean).join(' ').trim();
    if (!q) return null;

    try {
        const url = `https://photon.komoot.io/api/?q=${encodeURIComponent(q)}&limit=1&bbox=-74.1,-34.0,-28.6,5.3`;
        const resp = await fetch(url);
        if (resp.ok) {
            const data = await resp.json();
            const f = (data?.features || []).find((x: any) => x?.properties?.countrycode === 'BR' && x?.geometry?.coordinates);
            if (f) return { lat: f.geometry.coordinates[1], lng: f.geometry.coordinates[0] };
        }
    } catch { /* cai no Nominatim */ }

    try {
        const url = `https://nominatim.openstreetmap.org/search?format=json&countrycodes=br&limit=1&q=${encodeURIComponent(q)}`;
        const resp = await fetch(url, { headers: { 'Accept-Language': 'pt-BR' } });
        if (resp.ok) {
            const data = await resp.json();
            if (Array.isArray(data) && data[0]) return { lat: parseFloat(data[0].lat), lng: parseFloat(data[0].lon) };
        }
    } catch { /* sem coordenadas */ }

    return null;
};

// ── Monitoramento de Endereço (pernoite) ──────────────────────────────────

export interface LocacaoMonitoravel {
    idLocacao: string;
    idVeiculo: string;
    placa: string;
    idLocatario: string;
    nomeLocatario: string;
    logradouro?: string;
    numero?: string;
    bairro?: string;
    cidade?: string;
    estado?: string;
    cep?: string;
    endereco: string;
    latitude?: number | null;
    longitude?: number | null;
    localizado: boolean;
}

export interface PernoiteOcorrencia {
    nomeLocatario: string;
    placa: string;
    endereco: string;
    latitudeEndereco?: number | null;
    longitudeEndereco?: number | null;
    latitudeFora?: number | null;
    longitudeFora?: number | null;
    distanciaForaKm?: number | null;
    noitesFora: number;
    noitesConsideradas: number;
    noites: { noite: string; situacao: 'endereco' | 'fora' | 'sem_sinal' }[];
}

export const listarLocacoesMonitoramento = (idEmpresa: string) =>
    api.get(`/GestaoRiscoLocacoesMonitoramento/${idEmpresa}`).then(r => (r.data || []) as LocacaoMonitoravel[]);

export const salvarLocalizacaoLocatario = (idEmpresa: string, idLocatario: string, latitude: number, longitude: number, origem: 'busca' | 'mapa') =>
    api.put(`/GestaoRiscoLocalizacaoLocatario/${idLocatario}`, { idEmpresa, latitude, longitude, origem });

export const buscarPernoites = (idOcorrencia: string, idEmpresa: string) =>
    api.get(`/EventoRiscoOcorrencia/${idOcorrencia}/Pernoites`, { params: { idEmpresa } }).then(r => r.data as PernoiteOcorrencia);

// Localiza um endereço escrito livremente (Photon → Nominatim), restrito ao Brasil.
export const geocodificarTexto = async (q: string): Promise<{ lat: number; lng: number } | null> => {
    const consulta = q.trim();
    if (!consulta) return null;
    try {
        const url = `https://photon.komoot.io/api/?q=${encodeURIComponent(consulta)}&limit=1&bbox=-74.1,-34.0,-28.6,5.3`;
        const resp = await fetch(url);
        if (resp.ok) {
            const data = await resp.json();
            const f = (data?.features || []).find((x: any) => x?.properties?.countrycode === 'BR' && x?.geometry?.coordinates);
            if (f) return { lat: f.geometry.coordinates[1], lng: f.geometry.coordinates[0] };
        }
    } catch { /* tenta o Nominatim */ }
    try {
        const url = `https://nominatim.openstreetmap.org/search?format=json&countrycodes=br&limit=1&q=${encodeURIComponent(consulta)}`;
        const resp = await fetch(url, { headers: { 'Accept-Language': 'pt-BR' } });
        if (resp.ok) {
            const data = await resp.json();
            if (Array.isArray(data) && data[0]) return { lat: parseFloat(data[0].lat), lng: parseFloat(data[0].lon) };
        }
    } catch { /* sem resultado */ }
    return null;
};

// Localiza o endereço completo do locatário. Tenta com o número e, sem resultado, só a rua e depois o CEP.
export const geocodificarEndereco = async (l: Partial<LocacaoMonitoravel>): Promise<{ lat: number; lng: number } | null> => {
    const tentativas = [
        [l.logradouro, l.numero, l.bairro, l.cidade, l.estado],
        [l.logradouro, l.cidade, l.estado],
        [l.cep],
    ].map(p => p.filter(Boolean).join(', ').trim()).filter(q => q.length > 0);

    for (const q of tentativas) {
        const achado = await geocodificarTexto(q);
        if (achado) return achado;
    }
    return null;
};
